import { supabaseRestRequest } from './familyService';
import { runOnlineOrToast } from '../utils/networkGuard';

export interface Remedy {
  id: string;
  name: string;
  scientificName: string;
  commonNames: string[];
  description: string;
  mainIndications: string[];
  potencies: string[];
  symptoms: string[];
  mentalSymptoms: string[];
  physicalSymptoms: string[];
  modalities: {
    worse: string[];
    better: string[];
  };
  constitution: string;
  category: 'mineral' | 'plant' | 'animal' | 'nosode' | 'sarcodio' | 'autotherapeutic' | 'isotherapeutic';
  image: string;
  active: boolean;
  created: string;
  updated: string;
}

class RemedyService {
  private static instance: RemedyService;

  private constructor() {}

  public static getInstance(): RemedyService {
    if (!RemedyService.instance) {
      RemedyService.instance = new RemedyService();
    }
    return RemedyService.instance;
  }

  // Buscar todos os remédios ativos
  async getAllRemedies(): Promise<Remedy[]> {
    console.log('🔍 [RemedyService] Buscando todos os remédios');
    
    try {
      const data = await supabaseRestRequest(
        'remedies?active=eq.true&order=name.asc&select=*',
        'GET'
      );

      return this.formatRemedies(data);
    } catch (error: any) {
      console.error('❌ [RemedyService] Erro ao buscar remédios:', error);
      return [];
    }
  }

  // Buscar remédio por ID
  async getRemedyById(id: string): Promise<Remedy | null> {
    console.log('🔍 [RemedyService] Buscando remédio:', id);
    
    try {
      const data = await supabaseRestRequest(
        `remedies?id=eq.${id}&select=*`,
        'GET'
      );

      if (data && data.length > 0) {
        return this.formatRemedy(data[0]);
      }
      return null;
    } catch (error: any) {
      console.error('❌ [RemedyService] Erro ao buscar remédio:', error);
      return null;
    }
  }

  // Buscar remédios por categoria
  async getRemediesByCategory(category: string): Promise<Remedy[]> {
    console.log('🔍 [RemedyService] Buscando remédios da categoria:', category);
    
    try {
      if (category === 'all') {
        return this.getAllRemedies();
      }

      const data = await supabaseRestRequest(
        `remedies?category=eq.${category}&active=eq.true&order=name.asc&select=*`,
        'GET'
      );

      return this.formatRemedies(data);
    } catch (error: any) {
      console.error('❌ [RemedyService] Erro ao buscar remédios por categoria:', error);
      return [];
    }
  }

  // Buscar remédios (com filtro de texto)
  async searchRemedies(searchTerm: string): Promise<Remedy[]> {
    console.log('🔍 [RemedyService] Buscando remédios com termo:', searchTerm);
    
    try {
      // Buscar todos e filtrar localmente (mais simples)
      const allRemedies = await this.getAllRemedies();
      
      if (!searchTerm.trim()) {
        return allRemedies;
      }

      const term = searchTerm.toLowerCase();
      return allRemedies.filter(remedy => 
        remedy.name.toLowerCase().includes(term) ||
        remedy.scientificName.toLowerCase().includes(term) ||
        remedy.commonNames.some(name => name.toLowerCase().includes(term)) ||
        remedy.mainIndications.some(indication => indication.toLowerCase().includes(term)) ||
        remedy.symptoms.some(symptom => symptom.toLowerCase().includes(term))
      );
    } catch (error: any) {
      console.error('❌ [RemedyService] Erro ao buscar remédios:', error);
      return [];
    }
  }

  // Adicionar remédio (apenas admin)
  async addRemedy(remedy: Omit<Remedy, 'id' | 'created' | 'updated'>): Promise<Remedy | null> {
    console.log('➕ [RemedyService] Adicionando remédio:', remedy.name);
    
    try {
      const data = await runOnlineOrToast(async () =>
        supabaseRestRequest('remedies?select=*', 'POST', {
          name: remedy.name,
          scientific_name: remedy.scientificName,
          common_names: remedy.commonNames,
          description: remedy.description,
          main_indications: remedy.mainIndications,
          potencies: remedy.potencies,
          symptoms: remedy.symptoms,
          mental_symptoms: remedy.mentalSymptoms,
          physical_symptoms: remedy.physicalSymptoms,
          modalities_worse: remedy.modalities.worse,
          modalities_better: remedy.modalities.better,
          constitution: remedy.constitution,
          category: remedy.category,
          image_url: remedy.image,
          active: remedy.active
        })
      );

      console.log('✅ [RemedyService] Remédio adicionado:', data);
      return this.formatRemedy(Array.isArray(data) ? data[0] : data);
    } catch (error: any) {
      console.error('❌ [RemedyService] Erro ao adicionar remédio:', error);
      throw new Error(`Erro ao adicionar remédio: ${error.message}`);
    }
  }

  // Atualizar remédio (apenas admin)
  async updateRemedy(id: string, updates: Partial<Omit<Remedy, 'id' | 'created'>>): Promise<Remedy | null> {
    console.log('✏️ [RemedyService] Atualizando remédio:', id);
    
    try {
      const payload: any = {};
      
      if (updates.name) payload.name = updates.name;
      if (updates.scientificName) payload.scientific_name = updates.scientificName;
      if (updates.commonNames) payload.common_names = updates.commonNames;
      if (updates.description) payload.description = updates.description;
      if (updates.mainIndications) payload.main_indications = updates.mainIndications;
      if (updates.potencies) payload.potencies = updates.potencies;
      if (updates.symptoms) payload.symptoms = updates.symptoms;
      if (updates.mentalSymptoms) payload.mental_symptoms = updates.mentalSymptoms;
      if (updates.physicalSymptoms) payload.physical_symptoms = updates.physicalSymptoms;
      if (updates.modalities) {
        payload.modalities_worse = updates.modalities.worse;
        payload.modalities_better = updates.modalities.better;
      }
      if (updates.constitution) payload.constitution = updates.constitution;
      if (updates.category) payload.category = updates.category;
      if (updates.image) payload.image_url = updates.image;
      if (updates.active !== undefined) payload.active = updates.active;

      const data = await runOnlineOrToast(async () =>
        supabaseRestRequest(
          `remedies?id=eq.${id}&select=*`,
          'PATCH',
          payload
        )
      );

      console.log('✅ [RemedyService] Remédio atualizado:', data);
      return this.formatRemedy(Array.isArray(data) ? data[0] : data);
    } catch (error: any) {
      console.error('❌ [RemedyService] Erro ao atualizar remédio:', error);
      throw new Error(`Erro ao atualizar remédio: ${error.message}`);
    }
  }

  // Deletar remédio (apenas admin)
  async deleteRemedy(id: string): Promise<void> {
    console.log('🗑️ [RemedyService] Deletando remédio:', id);
    
    try {
      await runOnlineOrToast(async () =>
        supabaseRestRequest(`remedies?id=eq.${id}`, 'DELETE')
      );
      console.log('✅ [RemedyService] Remédio deletado com sucesso');
    } catch (error: any) {
      console.error('❌ [RemedyService] Erro ao deletar remédio:', error);
      throw new Error(`Erro ao deletar remédio: ${error.message}`);
    }
  }

  // Contar remédios por categoria
  async getCategoryCount(category: string): Promise<number> {
    try {
      if (category === 'all') {
        const remedies = await this.getAllRemedies();
        return remedies.length;
      }

      const remedies = await this.getRemediesByCategory(category);
      return remedies.length;
    } catch {
      return 0;
    }
  }

  // Formatar remédio do banco para o formato da aplicação
  private formatRemedy(data: any): Remedy {
    return {
      id: data.id,
      name: data.name,
      scientificName: data.scientific_name,
      commonNames: data.common_names || [],
      description: data.description,
      mainIndications: data.main_indications || [],
      potencies: data.potencies || [],
      symptoms: data.symptoms || [],
      mentalSymptoms: data.mental_symptoms || [],
      physicalSymptoms: data.physical_symptoms || [],
      modalities: {
        worse: data.modalities_worse || [],
        better: data.modalities_better || []
      },
      constitution: data.constitution || '',
      category: data.category,
      image: data.image_url || '',
      active: data.active,
      created: data.created_at,
      updated: data.updated_at
    };
  }

  // Formatar múltiplos remédios
  private formatRemedies(data: any[]): Remedy[] {
    return data.map(item => this.formatRemedy(item));
  }
}

export default RemedyService;
